/**
 * Policy / Content: who was asked to accept, and where they stand (source product's
 * AcceptanceStatsPanel). Recipients are created by the policy_distribution journey's segment
 * step — a rule over people, never a hand-made list. Status edits are coreBOS writes.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useList, useUpdate } from "@refinedev/core";
import { Button, Card, Col, Empty, Progress, Row, Select, Space, Statistic, Table, Typography } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { StatusTag } from "../fields";
import { valueLabel } from "./hints";

const STATUSES = ["Pending", "Read", "Accepted", "Final Acceptance", "Rejected"];
// Same values as the source product's evAcceptance.accmethod. Acceptance is recorded by staff for
// now (governance ADR-032 §7); "Portal" is reserved for the future self-service inbox.
const METHODS = [
  { value: "Manual", label: "Manuale (cartaceo / di persona)" },
  { value: "Email", label: "Email" },
  { value: "Firma Digital", label: "Firma digitale" },
  { value: "Portal", label: "Self-service", disabled: true },
];
const accepted = (s: unknown) => s === "Accepted" || s === "Final Acceptance";

export const AcceptancePanel: React.FC<{ record: Record<string, unknown>; linkField: "acctopolicy" | "acceptancetocontent"; journey?: string }> = ({ record, linkField, journey }) => {
  const navigate = useNavigate();
  const id = String(record.id);
  const { data, isLoading } = useList({ resource: "cmp_acceptance", filters: [{ field: linkField, operator: "eq", value: id }], pagination: { pageSize: 500 } });
  const { mutate } = useUpdate();
  const rows = (data?.data ?? []) as Array<Record<string, unknown>>;
  const n = (pred: (s: unknown) => boolean) => rows.filter((r) => pred(r.acceptance_status)).length;
  const ok = n(accepted);
  return <Card size="small" style={{ marginTop: 16 }} loading={isLoading}
    title={<Space><TeamOutlined />Accettazioni</Space>}
    extra={journey && <Button size="small" type="primary" onClick={() => navigate(`/wizard/${journey}`)}>Chiedi l'accettazione…</Button>}>
    {rows.length === 0
      ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna richiesta inviata. Scegli i destinatari con una regola (es. tutto l'ufficio HR)." />
      : <>
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={6}><Statistic title="Inviate" value={rows.length} /></Col>
          <Col span={6}><Statistic title="Accettate" value={ok} valueStyle={{ color: "#389e0d" }} /></Col>
          <Col span={6}><Statistic title="In attesa / lette" value={n((s) => s === "Pending" || s === "Read" || !s)} /></Col>
          <Col span={6}><Statistic title="Rifiutate" value={n((s) => s === "Rejected")} valueStyle={{ color: "#cf1322" }} /></Col>
        </Row>
        <Progress percent={Math.round((ok / rows.length) * 100)} />
        <Table size="small" rowKey={(r) => String(r.id)} dataSource={rows} pagination={{ pageSize: 10 }} style={{ marginTop: 12 }}
          columns={[
            { title: "Persona", render: (_, r) => String(r.acctitle ?? r.pacclinktocontact ?? r.id) },
            { title: "Stato", width: 200, filters: STATUSES.map((s) => ({ text: valueLabel(s), value: s })), onFilter: (v, r) => r.acceptance_status === v,
              render: (_, r) => <Select size="small" style={{ width: 180 }} value={r.acceptance_status as string}
                options={STATUSES.map((s) => ({ value: s, label: <StatusTag value={s} /> }))}
                onChange={(v: string) => mutate({ resource: "cmp_acceptance", id: String(r.id), values: {
                  acceptance_status: v, acclastdate: dayjs().format("YYYY-MM-DD"),
                  // recorded by staff: say so unless a method is already set
                  ...(accepted(v) && !r.accmethod ? { accmethod: "Manual" } : {}) } })} /> },
            { title: "Modalità", width: 220, render: (_, r) => <Select size="small" style={{ width: 200 }} allowClear placeholder="—"
                value={(r.accmethod as string) || undefined} options={METHODS}
                onChange={(v?: string) => mutate({ resource: "cmp_acceptance", id: String(r.id), values: { accmethod: v ?? "" } })} /> },
            { title: "Ultimo aggiornamento", dataIndex: "acclastdate", render: (v) => v ? dayjs(String(v)).format("DD/MM/YYYY") : "—" },
          ]} />
        <Typography.Text type="secondary">Registra qui l'accettazione raccolta su carta, per email o con firma digitale: le verifiche leggono gli stessi dati.</Typography.Text>
      </>}
  </Card>;
};
